/*jshint esversion: 6 */
/*
 * signInProgress — lightweight feedback for the SSO sign-in bounce.
 *
 * LogRocket showed sign-in redirects taking 24–78s with no indicator, so users
 * assumed the page was broken. Rather than a full-page blocking overlay, this
 * shows a slim animated progress bar pinned to the top of the viewport.
 *
 * Because the SSO flow is a series of full-page navigations (current page →
 * identity provider → callback → post-auth page), a one-page click handler
 * isn't enough. We persist a "signing in" flag in sessionStorage so the bar
 * re-appears on each same-origin page during the bounce, and clear it once the
 * post-auth page renders (detected by the authenticated-only ".signout" link)
 * or after a safety timeout so it can never get stuck.
 */

const SIGNING_IN_KEY = 'asaSigningIn';
const SIGNING_IN_AT_KEY = 'asaSigningInAt';
const BAR_ID = 'asa-signin-progress';
const STYLE_ID = 'asa-signin-progress-styles';

// Upper bound on how long we keep showing the bar before assuming the flow was
// abandoned (e.g. the user backed out of the IdP). Comfortably above the worst
// observed redirect time (78s).
const MAX_DURATION_MS = 120000;

function now() {
    return new Date().getTime();
}

function isAuthenticated() {
    // The header only renders a ".signout" control for authenticated users.
    return !!document.querySelector('.signout');
}

function injectStylesOnce() {
    if (document.getElementById(STYLE_ID)) return;

    // z-index sits above the fixed header/notification stack — .header-wrapper and
    // .site-header are z-index:1000 and the alert/promo/coupon/reminder banners are
    // 99–100 — so the bar is never hidden by them. pointer-events:none means the bar
    // also never blocks clicks on those banners (e.g. their close buttons), so the two
    // coexist regardless of which is on screen.
    const css = `
#${BAR_ID}{position:fixed;top:0;left:0;height:3px;width:100%;z-index:2147483646;
background:transparent;overflow:hidden;display:none;pointer-events:none;}
#${BAR_ID}.visible{display:block;}
#${BAR_ID} .asa-signin-progress__fill{position:absolute;top:0;left:0;height:100%;width:40%;
background:#005eb8;animation:asaSignInProgressSlide 1.1s ease-in-out infinite;}
@keyframes asaSignInProgressSlide{
0%{left:-40%;width:40%;}50%{left:30%;width:55%;}100%{left:100%;width:40%;}}`;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
}

function getBar() {
    let bar = document.getElementById(BAR_ID);
    if (!bar) {
        bar = document.createElement('div');
        bar.id = BAR_ID;
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-label', 'Signing you in');

        const fill = document.createElement('div');
        fill.className = 'asa-signin-progress__fill';
        bar.appendChild(fill);

        document.body.appendChild(bar);
    }
    return bar;
}

function show() {
    injectStylesOnce();
    getBar().classList.add('visible');
}

function hide() {
    const bar = document.getElementById(BAR_ID);
    if (bar) bar.classList.remove('visible');
}

/**
 * Begin the signing-in indication. Call on a sign-in / re-auth click, before the
 * navigation to the identity provider happens.
 */
function start() {
    try {
        sessionStorage.setItem(SIGNING_IN_KEY, '1');
        sessionStorage.setItem(SIGNING_IN_AT_KEY, String(now()));
    } catch (e) { /* sessionStorage may be unavailable; bar still shows this page */ }
    show();
}

/** Clear the signing-in indication. */
function stop() {
    try {
        sessionStorage.removeItem(SIGNING_IN_KEY);
        sessionStorage.removeItem(SIGNING_IN_AT_KEY);
    } catch (e) { /* no-op */ }
    hide();
}

/**
 * Decide whether to show the bar on this page load. Run on every page so the bar
 * survives the multi-hop SSO bounce and is dismissed once we land authenticated.
 */
let abandonGuardsAttached = false;

// While the bar is up the user should just be waiting on automatic SSO redirects.
// If they actually interact with the page, they're looking at real content (e.g.
// they hit Back), so treat it as an abandoned sign-in and clear the bar.
function attachAbandonGuards() {
    if (abandonGuardsAttached || typeof window === 'undefined') return;
    abandonGuardsAttached = true;

    const onInteract = function () {
        window.removeEventListener('pointerdown', onInteract, true);
        window.removeEventListener('keydown', onInteract, true);
        abandonGuardsAttached = false;
        stop();
    };

    window.addEventListener('pointerdown', onInteract, true);
    window.addEventListener('keydown', onInteract, true);
}

function initOnLoad() {
    let signingIn = false;
    let startedAt = 0;
    try {
        signingIn = sessionStorage.getItem(SIGNING_IN_KEY) === '1';
        startedAt = parseInt(sessionStorage.getItem(SIGNING_IN_AT_KEY), 10) || 0;
    } catch (e) { /* no-op */ }

    if (!signingIn) {
        hide();
        return;
    }

    // Post-auth page rendered, or we've waited too long — either way, dismiss.
    if (isAuthenticated() || (startedAt && (now() - startedAt) > MAX_DURATION_MS)) {
        stop();
        return;
    }

    show();
    attachAbandonGuards();
}

// pageshow fires on normal loads AND on back/forward (bfcache) restores, where
// $(document).ready does not run. event.persisted === true means a bfcache
// restore: the user navigated Back to a page they'd already seen, so the sign-in
// was abandoned — clear the bar. Otherwise apply the usual dismissal rules.
function handlePageShow(event) {
    if (event && event.persisted) {
        stop();
        return;
    }
    initOnLoad();
}

if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', handlePageShow);
}

const signInProgress = { start, stop, initOnLoad };

export default signInProgress;
